/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './zohoInvoiceDetails.scss';
import { useAuth } from '../../Hooks/useAuth';
import { appendServiceNameToURL, appendServicePortToURL } from '../../Api/resources/serviceURLs';
import { serviceName } from '../../Api/resources/serviceConstants';
import { ZOHO_INVOICE_DETAILS } from '../../Api/resources/endPoint';
import { getExportFileName, getExportFileNameWithDate } from '../Delivery/utils';
import AddEditDialog from './addEditDialog';
import ZohoUploadDialog from './zohoUploadDialog';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { Tooltip } from 'primereact/tooltip';
import { MODULE_NAMES } from '../../Constants/modules';

const UNPAGINATED_LIMIT = 0;

// Build service URL once (module level, no re-runs)
const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL + url + path;
  }
  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return `${baseUrl}:${port}${url}${path}`;
};

const baseURL = buildServiceURL(serviceName.MASTER_MS, '/api' + ZOHO_INVOICE_DETAILS);

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;
  const toolbarButtons = rootNode.querySelectorAll('.button-group button');
  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isDownloadButton
      = !isFilterButton && !isResetButton && Boolean(buttonElement.querySelector('svg'));
    buttonElement.classList.toggle('zoho-invoice-details-reset-btn', isResetButton);
    buttonElement.classList.toggle('zoho-invoice-details-export-btn', isDownloadButton);
  });
};

const ZohoInvoiceDetails = () => {
  const { userProfile } = useAuth();
  const tableContainerRef = useRef(null);
  const tableStateRef = useRef(null);
  const moreMenuRef = useRef(null);
  const unpaginatedPagination = useMemo(() => ({
    page: 1,
    limit: UNPAGINATED_LIMIT,
    total: 0,
    showPageSizeSelector: false,
  }), []);
  const [dialog, setDialog] = useState({
    open: false,
    mode: 'add',
    data: null,
  });

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Response handler — append total row from API response
  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    const tableData
  = response?.data?.tableData
  || response?.data?.data?.tableData
  || [];

    const totalRow
  = response?.data?.totalRow
  || response?.data?.data?.totalRow;

    if (tableData.length && totalRow) {
      const updatedData = [...tableData, totalRow];

      if (response?.data?.tableData) {
        response.data.tableData = updatedData;
      } else if (response?.data?.data?.tableData) {
        response.data.data.tableData = updatedData;
      }
    }
  }, []);

  // Permission logic
  const zohoInvoicePermissions = userProfile?.permissions?.[MODULE_NAMES.FINANCE_ZOHO_INVOICE_DETAILS] || [];
  const canAdd = zohoInvoicePermissions.includes('create');
  const canEdit = zohoInvoicePermissions.includes('update');

  // Build total row

  const handleAddIconClick = () => {
    if (!canAdd) {
      toast.error?.('You do not have permission to add invoices.');
      return;
    }
    setDialog({ open: true, mode: 'add', data: null });
  };

  const handleUploadDialogOpen = useCallback(() => setUploadDialogOpen(true), []);
  const handleUploadDialogClose = () => setUploadDialogOpen(false);

  const moreMenuItems = useMemo(() => [
    {
      label: 'Upload Zoho Invoice Details',
      icon: 'pi pi-upload',
      command: handleUploadDialogOpen,
    },
  ], [handleUploadDialogOpen]);

  const additionalElements = useMemo(() => [
    <React.Fragment key="zoho-more-menu">
      <Menu
        ref={moreMenuRef}
        model={moreMenuItems}
        popup
        appendTo={document.body}
        className="zoho-invoice-more-menu"
      />
      <Button
        icon="pi pi-ellipsis-v"
        className="p-button-text zoho-invoice-more-btn"
        onClick={(e) => moreMenuRef.current?.toggle(e)}
        tooltip="More options"
        tooltipOptions={{ position: 'top', className: 'zoho-invoice-more-tooltip' }}
      />
    </React.Fragment>,
  ], [moreMenuItems]);

  const handleCellClick = useCallback((params) => {
    const { action, rowData } = params || {};
    // Prevent actions on total row
    if (rowData?.isTotalRow) {
      return;
    }
    if (action === 'edit') {
      if (!canEdit) {
        toast.error?.('You do not have permission to edit invoices.');
        return;
      }
      setDialog({ open: true, mode: 'edit', data: rowData });
    }
  }, [canEdit]);

  // Helper to refresh the table with current filters
  const refreshCurrentTableWithFilters = useCallback(() => {
    const tableState = tableStateRef.current?.();
    if (tableState?.fetchTableData) {
      tableState.fetchTableData(
        tableState.filters,
        unpaginatedPagination,
        tableState.searchQuery,
      );
    }
  }, [unpaginatedPagination]);
  // Toolbar button class
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const timer = setTimeout(() => applyToolbarButtonClasses(container), 500);
    const toolbarObserver = new MutationObserver(() => applyToolbarButtonClasses(container));
    toolbarObserver.observe(container, { childList: true, subtree: true });
    return () => {
      clearTimeout(timer);
      toolbarObserver.disconnect();
    };
  }, []);

  // Add info icon to Zoho Invoice Status header
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    // Shared tooltip
    const tooltip = document.createElement('div');
    tooltip.className = 'zoho-status-custom-tooltip';
    tooltip.innerHTML = `
      1 - Pending to Collect<br/>
      2 - Closed<br/>
      3 - Partially Paid<br/>
      4 - Paid
    `;

    document.body.appendChild(tooltip);

    const showTooltip = (e) => {
      const rect = e.target.getBoundingClientRect();

      tooltip.style.display = 'block';
      tooltip.style.top = `${rect.top + window.scrollY - tooltip.offsetHeight - 8}px`;
      tooltip.style.left = `${rect.left + window.scrollX + rect.width / 2}px`;
      tooltip.style.transform = 'translateX(-50%)';
    };

    const hideTooltip = () => {
      tooltip.style.display = 'none';
    };

    const addStatusInfoIcon = () => {
      const headers = container.querySelectorAll('.p-datatable-thead th');

      headers.forEach((header) => {
        const headerText = header.textContent?.trim();

        if (!headerText.includes('Zoho Invoice Status')) return;

        // Correct target from your DOM
        const targetDiv = header.querySelector('.header-column');

        if (!targetDiv) return;

        // Prevent duplicates
        if (targetDiv.querySelector('.zoho-status-info-icon')) return;

        const infoIcon = document.createElement('i');

        infoIcon.className
        = 'pi pi-info-circle zoho-status-info-icon';

        infoIcon.style.marginLeft = '6px';
        infoIcon.style.cursor = 'pointer';
        infoIcon.style.fontSize = '14px';

        infoIcon.addEventListener('mouseenter', showTooltip);
        infoIcon.addEventListener('mouseleave', hideTooltip);

        targetDiv.appendChild(infoIcon);
      });
    };

    const observer = new MutationObserver(() => {
      addStatusInfoIcon();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    addStatusInfoIcon();

    return () => {
      observer.disconnect();

      tooltip.remove();
    };
  }, []);

  //  Auto right-align numeric columns
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const applyRightAlign = () => {
      const rows = container.querySelectorAll('.p-datatable-tbody tr');
      const columnStats = new Map();
      rows.forEach((row) => {
        row.querySelectorAll('td').forEach((cell) => {
          const isTotalRow = row.textContent?.includes('Total');

          if (isTotalRow) {
            row.classList.add('zoho-total-row');
          }
          const colIndex = cell.cellIndex;
          if (!columnStats.has(colIndex)) {
            columnStats.set(colIndex, { number: 0, text: 0 });
          }
          const stats = columnStats.get(colIndex);
          const cleaned = (cell.textContent ?? '').replace(/\u00A0/g, ' ').trim();
          if (cleaned === '') return;
          const normalized = cleaned.replace(/[−–—]/g, '-');
          const isDash = normalized === '-';
          const numericCandidate = normalized.replace(/,/g, '').replace(/\s+/g, '');
          const isNumber = /^-?\d+(\.\d+)?%?$/.test(numericCandidate);
          if (isNumber) stats.number++;
          else if (!isDash) stats.text++;
        });
      });

      rows.forEach((row) => {
        row.querySelectorAll('td').forEach((cell) => {
          const colIndex = cell.cellIndex;
          const stats = columnStats.get(colIndex);
          if (!stats) return;
          cell.classList.toggle('right-align', stats.number > 0 && stats.text === 0);
        });
      });

      container.querySelectorAll('.p-datatable-thead th').forEach((header) => {
        const stats = columnStats.get(header.cellIndex);
        header.classList.toggle(
          'right-align',
          Boolean(stats?.number > 0 && stats?.text === 0),
        );
      });
    };

    const observer = new MutationObserver(applyRightAlign);
    observer.observe(container, { childList: true, subtree: true, characterData: true });
    applyRightAlign();
    return () => observer.disconnect();
  }, []);

  // Static configuration
  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        headers: { token: userProfile?.access_token || '' },
        additionalPayloadData: {
          employeeId: userProfile?.employeeId || '',
        },
        userInfo: {
          id: userProfile?.id || '',
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || [],
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
        },
        handlers: {
          handleAddIconClick,
        },
        handler: {
          handleFilteredApiResponse,
          onCellClickHandler: handleCellClick,
          setTableStateHandler: (getState) => {
            tableStateRef.current = getState;
          },
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => getExportFileName('Zoho_Invoice_Details'),
            sheetName: () => getExportFileNameWithDate('Zoho_Invoice_Details'),
          },
        },
        showToolBar: true,
        showPagination: false,
        toolBarSettings: {
          showAddNew: true,
          showFilterOption: true,
          showExpandCollapse: false,
          showDownloadExcel: true,
          downloadExcelSheetName: 'Zoho Invoice Details',
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showToolBarHeading: true,
          toolBarHeading: 'Zoho Invoice Details',
          showReset: true,
          additionalElements,
        },
      },
      showFilterOption: true,
      tableSettings: {
        enableTreeTable: false,
        size: 'large',
        loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
        paginationConfig: unpaginatedPagination,
        showExpandCollapse: false,
        tableScrollHeight: '400px',
        maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
      },
    }),
    [userProfile, handleFilteredApiResponse, additionalElements, unpaginatedPagination],
  );

  return (<>
    <div className="zoho-invoice-details-outer">
      <div className="zoho-invoice-details-table">
        <div className="zoho-invoice-details-table-utility" ref={tableContainerRef}>
          <TableUtility configuration={configuration} />
        </div>
      </div>
    </div>
    <AddEditDialog
      open={dialog.open}
      mode={dialog.mode}
      initialData={dialog.data}
      onClose={() => setDialog({ open: false })}
      baseURL={baseURL}
      headers={{ token: userProfile?.access_token }}
      onSuccess={() => {
        setDialog({ open: false });
        refreshCurrentTableWithFilters();
      }}
    />
    <ZohoUploadDialog
      open={uploadDialogOpen}
      onClose={handleUploadDialogClose}
      baseURL={baseURL}
      headers={{ token: userProfile?.access_token }}
      onSuccess={refreshCurrentTableWithFilters}
    />
    <Tooltip
      target=".zoho-status-info-icon"
      className="zoho-status-tooltip"
    />
  </>
  );
};

export default ZohoInvoiceDetails;
